import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faNewspaper,
  faCalendarAlt,
  faExclamationTriangle,
  faBell,
  faShieldAlt,
  faInfoCircle
} from "@fortawesome/free-solid-svg-icons";
import { motion } from "framer-motion";
import PropTypes from "prop-types";

// Strip HTML tags for sidebar description previews
const stripHtml = (html) => {
  if (!html) return '';
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return (doc.body.textContent || '').replace(/\s+/g, ' ').trim();
};

const Sidebar = ({
  primaryItems = [], // Renamed from 'news'
  secondaryItems = [],
  counts = {},
  loading = false,
  primaryTitle = "news", // Still used to determine content type
  secondaryTitle = "events",
  countTitle = "",
  getCountIcon = () => null,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const recentPrimaryItems = primaryItems // Renamed from 'recentNews'
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
    .slice(0, 3);
  const recentSecondaryItems = secondaryItems
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
    .slice(0, 3);

  // Get appropriate fallback icon based on content type
  const getFallbackIcon = (type) => {
    const iconMap = {
      news: faNewspaper,
      events: faCalendarAlt,
      alerts: faExclamationTriangle,
      advisories: faShieldAlt,
      notifications: faBell,
    };
    return iconMap[type] || faInfoCircle;
  };

  // Get gradient colors based on content type (using primary color to match News list)
  const getFallbackGradient = (type) => {
    // Use primary color gradient for all types to maintain consistency
    return "from-primary/10 to-primary/20";
  };

  // Get icon color based on content type (using primary color to match News list)
  const getFallbackIconColor = (type) => {
    // Use primary color for all types to maintain consistency
    return "text-primary/40";
  };

  // Determine the header title based on primaryTitle and secondaryTitle
  const headerKey = `${primaryTitle}_${secondaryTitle}`;
  const headerTitle =
    headerKey === "news_events"
      ? t(
        "header.news_and_events",
        `${t("news.title")} ${t("events.and")} ${t("events.title")}`
      )
      : headerKey === "news_alerts"
        ? t(
          "header.news_and_alerts",
          `${t("news.title")} ${t("events.and")} ${t("alerts.title")}`
        )
        : `${t(`${primaryTitle}.title`)} ${t("events.and")} ${t(
          `${secondaryTitle}.title`
        )}`;

  // Check if this is an Alerts or Events page with statistics
  const isStatsFirst =
    countTitle === "alerts.title" || countTitle === "events.title";

  // Primary Items Section (generic for news, events, alerts, etc.)
  const PrimaryItemsSection = (loading || recentPrimaryItems.length > 0) ? (
    <div>
      <div className="mb-6">
        <h3 className="font-catamaran text-lg font-bold text-gray-900 mb-4">
          {t(`${primaryTitle}.title`)}
        </h3>
        {loading ? (
          <div className="space-y-3">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="animate-pulse flex gap-3">
                <div className="w-16 h-12 bg-gray-200 rounded-lg"></div>
                <div className="flex-1 space-y-2">
                  <div className="h-3 bg-gray-200 rounded w-3/4"></div>
                  <div className="h-2 bg-gray-200 rounded w-1/2"></div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="space-y-4">
            {recentPrimaryItems.map((item) => (
              <motion.div
                key={item.id}
                whileHover={{ y: -2 }}
                className="group flex gap-3 p-3 bg-gray-50 hover:bg-white rounded-xl border border-transparent hover:border-gray-200 hover:shadow-md transition-all duration-300 cursor-pointer"
                onClick={() =>
                  navigate(
                    `/${primaryTitle}/${item.slug || item.id
                    }`
                  )
                }
              >
                <div className="w-1/2 aspect-[2/1] rounded-lg overflow-hidden flex-shrink-0 bg-gray-200">
                  {(item.media_type === "video" || (item.image && (item.image.includes('youtube.com') || item.image.includes('youtu.be') || item.image.includes('vimeo.com')))) ? (
                    <div className="relative w-full h-full bg-gray-900 flex items-center justify-center">
                      {item.video_file ? (
                        <video
                          src={item.video_file}
                          className="w-full h-full object-cover"
                          autoPlay
                          muted
                          loop
                        />
                      ) : (
                        <iframe
                          src={(() => {
                            const videoUrl = item.video || item.image;
                            if (videoUrl.includes("youtube.com") || videoUrl.includes("youtu.be")) {
                              const videoId = videoUrl.includes("youtube.com")
                                ? videoUrl.split("v=")[1]?.split("&")[0]
                                : videoUrl.split("youtu.be/")[1]?.split("?")[0];
                              return `https://www.youtube.com/embed/${videoId}?autoplay=1&mute=1&loop=1&playlist=${videoId}`;
                            } else if (videoUrl.includes("vimeo.com")) {
                              const videoId = videoUrl.split("vimeo.com/")[1]?.split("?")[0];
                              return `https://player.vimeo.com/video/${videoId}?autoplay=1&muted=1&loop=1`;
                            }
                            return videoUrl;
                          })()
                          }
                          className="w-full h-full"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                          title={item.translations?.title || item.title || "Video"}
                        />
                      )}
                    </div>
                  ) : (item.image_file || item.image) ? (
                    <img
                      src={item.image_file || item.image}
                      alt={item.translations?.title || item.title || "Item"}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  ) : (
                    // Fallback for items without images
                    <div className={`w-full h-full bg-gradient-to-br ${getFallbackGradient(primaryTitle)} flex items-center justify-center`}>
                      <FontAwesomeIcon
                        icon={getFallbackIcon(primaryTitle)}
                        className={`text-2xl ${getFallbackIconColor(primaryTitle)}`}
                      />
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="font-nunito-sans text-sm font-semibold text-gray-900 group-hover:text-primary transition-colors duration-300 line-clamp-2 leading-tight mb-1">
                    {item.translations?.title || item.title || "No Title"}
                  </h4>
                  <p className="font-nunito-sans text-xs text-gray-600 line-clamp-2 leading-relaxed">
                    {stripHtml(
                      item.translations?.description ||
                        item.description ||
                        "No Description"
                    )}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  ) : null;

  // Secondary Items Section (e.g., Events, Alerts)
  const SecondaryItemsSection = (loading || recentSecondaryItems.length > 0) ? (
    <div>
      <div className="mb-6">
        <h3 className="font-catamaran text-lg font-bold text-gray-900 mb-4">
          {t(`${secondaryTitle}.title`)}
        </h3>
        {loading ? (
          <div className="space-y-3">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="animate-pulse flex gap-3">
                <div className="w-16 h-12 bg-gray-200 rounded-lg"></div>
                <div className="flex-1 space-y-2">
                  <div className="h-3 bg-gray-200 rounded w-3/4"></div>
                  <div className="h-2 bg-gray-200 rounded w-1/2"></div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="space-y-4">
            {recentSecondaryItems.map((item) => (
              <motion.div
                key={item.id}
                whileHover={{ y: -2 }}
                className="group flex gap-3 p-3 bg-gray-50 hover:bg-white rounded-xl border border-transparent hover:border-gray-200 hover:shadow-md transition-all duration-300 cursor-pointer"
                onClick={() =>
                  navigate(`/${secondaryTitle}/${item.slug || item.id}`)
                }
              >
                <div className="w-1/2 aspect-[2/1] rounded-lg overflow-hidden flex-shrink-0 bg-gray-200">
                  {(item.media_type === "video" || (item.image && (item.image.includes('youtube.com') || item.image.includes('youtu.be') || item.image.includes('vimeo.com')))) ? (
                    <div className="relative w-full h-full bg-gray-900 flex items-center justify-center">
                      {item.video_file ? (
                        <video
                          src={item.video_file}
                          className="w-full h-full object-cover"
                          autoPlay
                          muted
                          loop
                        />
                      ) : (
                        <iframe
                          src={(() => {
                            const videoUrl = item.video || item.image;
                            if (videoUrl.includes("youtube.com") || videoUrl.includes("youtu.be")) {
                              const videoId = videoUrl.includes("youtube.com")
                                ? videoUrl.split("v=")[1]?.split("&")[0]
                                : videoUrl.split("youtu.be/")[1]?.split("?")[0];
                              return `https://www.youtube.com/embed/${videoId}?autoplay=1&mute=1&loop=1&playlist=${videoId}`;
                            } else if (videoUrl.includes("vimeo.com")) {
                              const videoId = videoUrl.split("vimeo.com/")[1]?.split("?")[0];
                              return `https://player.vimeo.com/video/${videoId}?autoplay=1&muted=1&loop=1`;
                            }
                            return videoUrl;
                          })()
                          }
                          className="w-full h-full"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                          title={item.translations?.title || item.title || "Video"}
                        />
                      )}
                    </div>
                  ) : (item.image || item.file_url || item.image_file) ? (
                    <img
                      src={item.image || item.file_url || item.image_file}
                      alt={item.translations?.title || item.title || "Item"}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  ) : (
                    // Fallback for items without images
                    <div className={`w-full h-full bg-gradient-to-br ${getFallbackGradient(secondaryTitle)} flex items-center justify-center`}>
                      <FontAwesomeIcon
                        icon={getFallbackIcon(secondaryTitle)}
                        className={`text-2xl ${getFallbackIconColor(secondaryTitle)}`}
                      />
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="font-nunito-sans text-sm font-semibold text-gray-900 group-hover:text-primary transition-colors duration-300 line-clamp-2 leading-tight mb-1">
                    {item.translations?.title || item.title || "No Title"}
                  </h4>
                  <p className="font-nunito-sans text-xs text-gray-600 line-clamp-2 leading-relaxed">
                    {stripHtml(
                      item.translations?.description ||
                        item.description ||
                        "No Description"
                    )}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  ) : null;

  // Counts Section (Statistics)
  const CountsSection =
    countTitle && Object.keys(counts).length > 0 ? (
      <div>
        <div className="mb-6">
          <h3 className="font-catamaran text-lg font-bold text-gray-900 mb-4">
            {countTitle === "alerts.title" || countTitle === "events.title"
              ? t(`${countTitle.replace(".title", ".title")}`)
              : t(countTitle)}
          </h3>
          {loading ? (
            <div className="space-y-3">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="animate-pulse flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-4 h-4 bg-gray-200 rounded"></div>
                    <div className="h-3 bg-gray-200 rounded w-20"></div>
                  </div>
                  <div className="w-8 h-6 bg-gray-200 rounded-full"></div>
                </div>
              ))}
            </div>
          ) : (
            <div className="space-y-3">
              {Object.entries(counts).map(([key, count]) => (
                <div key={key} className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {getCountIcon(key)}
                    <span className="capitalize font-nunito-sans font-medium text-sm text-gray-800">
                      {key === "all"
                        ? countTitle === "alerts.title"
                          ? t("alerts.severities.all")
                          : t("events.all_status")
                        : t(
                          `${countTitle === "alerts.title"
                            ? "alerts.severities"
                            : "events.status"
                          }.${key}`
                        )}
                    </span>
                  </div>
                  <div className="bg-gray-100 rounded-full px-3 py-1 font-nunito-sans text-sm font-medium">
                    {count}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    ) : null;

  // Determine if we have any content to show
  const hasContent = PrimaryItemsSection || SecondaryItemsSection || CountsSection;

  // Don't render sidebar if no content
  if (!hasContent) {
    return null;
  }

  return (
    <div className="hidden xl:block w-full pl-8 mb-16">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="bg-white rounded-2xl border border-gray-100 shadow-lg hover:shadow-xl transition-shadow duration-300 p-8 sticky top-24 max-xl:top-32 relative overflow-hidden"
      >
        {/* Modern Decorative Background */}
        <div className="absolute -top-4 -right-4 w-24 h-24 bg-gradient-to-br from-primary/5 to-primary/10 rounded-full blur-2xl"></div>
        <div className="absolute -bottom-4 -left-4 w-32 h-32 bg-gradient-to-tr from-blue-50 to-indigo-50 rounded-full blur-3xl"></div>

        <div className="relative z-10">
          <div className="mb-8">
            <h2 className="font-catamaran text-2xl font-bold text-gray-900 mb-2">
              {headerTitle}
            </h2>
            <div className="w-12 h-1 bg-gradient-to-r from-primary to-primary/60 rounded-full"></div>
          </div>

          <div className="space-y-8">
            {PrimaryItemsSection}
            {SecondaryItemsSection}
            {isStatsFirst && CountsSection}
            {!isStatsFirst && CountsSection}
          </div>
        </div>
      </motion.div>
    </div>
  );
};

Sidebar.propTypes = {
  primaryItems: PropTypes.arrayOf( // Renamed from 'news'
    PropTypes.shape({
      id: PropTypes.number,
      title: PropTypes.string,
      description: PropTypes.string,
      image_file: PropTypes.string,
      image: PropTypes.string,
      slug: PropTypes.string, // Added for flexibility
      translations: PropTypes.shape({
        title: PropTypes.string,
        description: PropTypes.string,
      }),
      created_at: PropTypes.string,
    })
  ),
  secondaryItems: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      title: PropTypes.string,
      description: PropTypes.string,
      image: PropTypes.string,
      file_url: PropTypes.string,
      slug: PropTypes.string,
      translations: PropTypes.shape({
        title: PropTypes.string,
        description: PropTypes.string,
      }),
      created_at: PropTypes.string,
    })
  ),
  counts: PropTypes.object,
  loading: PropTypes.bool,
  primaryTitle: PropTypes.string,
  secondaryTitle: PropTypes.string,
  countTitle: PropTypes.string,
  getCountIcon: PropTypes.func,
};

export default Sidebar;